export interface MaterializeFilesResult {
  /** Memory-backed copies of the files that could be read. */
  materialized: File[];
  /** Names of files whose content could no longer be read from disk. */
  unreadable: string[];
}

/**
 * Copies picker-provided Files into memory-backed Files at attach time.
 *
 * WebKit keeps picked Files as live references to the file on disk: if the
 * file is moved, deleted, or modified after being attached (typical with
 * macOS screenshots), the submit-time read fails silently and Safari sends
 * the request as an empty multipart body without a `boundary` parameter,
 * which the BFF cannot parse. Reading the bytes here pins the content in
 * memory so later submits no longer depend on the file still existing.
 *
 * Files that are already unreadable are skipped (without failing the rest
 * of the batch) and reported back so the caller can ask the user to
 * re-attach them.
 */
export async function materializeFiles(files: File[]): Promise<MaterializeFilesResult> {
  const materialized: File[] = [];
  const unreadable: string[] = [];
  for (const file of files) {
    try {
      const buffer = await file.arrayBuffer();
      materialized.push(new File([buffer], file.name, { type: file.type }));
    } catch {
      unreadable.push(file.name);
    }
  }
  return { materialized, unreadable };
}
